import { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BtnTeal, Pan, PanCard } from '@quizzach/styles';
import { IcPlayOutline, LogoWordmark, QLogo, Xcollab } from '@quizzach/assets';
import { convertUnixTimestamp } from '@quizzach/utils';
import { RoutePath } from '@quizzach/interfaces';
import { CardAuthorMark } from '@quizzach/components';

const STANDARD_COVER = '/quiz-play-backgrounds/default_play_cover.webp';

/* the minimal quiz shape the ticket needs — IUserQuizzes satisfies it, and
   order cart items can be mapped onto it without the full premade class */
export interface ITicketQuiz {
	uuid: string;
	quiz_type: string;
	created_at: number;
	number_of_questions: number;
	fresh?: boolean;
	premade_quiz?: {
		title: string;
		cover_photo_small: string;
		author: { display_name: string };
	} | null;
}

interface QuizTicketProps {
	quiz: ITicketQuiz;
	className?: string;
	style?: CSSProperties;
}

const useQuizBits = (quiz: ITicketQuiz) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { uuid, quiz_type, premade_quiz } = quiz;
	const isPremade = quiz_type === 'premade';
	const cover = isPremade
		? `${
				import.meta.env.VITE_ASSETS_URL
			}/thematic_quizzes/${premade_quiz?.cover_photo_small}`
		: STANDARD_COVER;
	const title = isPremade
		? premade_quiz?.title
		: quiz_type === 'quick'
			? t('profile.quizCard.generalKnowledge')
			: t('profile.quizCard.configured');
	const authorName = premade_quiz?.author?.display_name;
	return {
		cover,
		title,
		shortId: uuid.substring(uuid.length - 8),
		showAuthor: isPremade && !!authorName && authorName !== 'quizzach',
		authorName,
		typeLabel: isPremade
			? t('profile.quizzes.premadeType')
			: quiz_type === 'quick'
				? t('profile.quizzes.quickType')
				: t('profile.quizzes.generatorType'),
		startQuiz: () => navigate(`/quiz/${uuid}/${RoutePath.QuizPlay}`),
	};
};

/* author signature in brand color: authored premades get Q ✕ author
   logo/name, everything else the quizzach wordmark */
const AuthorSignature = ({
	showAuthor,
	authorName,
}: {
	showAuthor: boolean;
	authorName: string | undefined;
}) => {
	const { t } = useTranslation();
	return showAuthor && authorName ? (
		<span
			className='amark'
			aria-label={t('cart.quizType.byAuthor', { author: authorName })}
		>
			<QLogo size={22} />
			<Xcollab size={11} />
			<CardAuthorMark displayName={authorName} />
		</span>
	) : (
		<span className='amark' aria-label='quizzach'>
			<LogoWordmark width={72} height={22} />
		</span>
	);
};

/* grid mode (and every viewport below the desktop breakpoint): the
   upright panorama card — untouched 3:2 cover on top, title on paper */
export const QuizTicketCard = ({ quiz, className, style }: QuizTicketProps) => {
	const { t } = useTranslation();
	const { created_at, number_of_questions, fresh } = quiz;
	const { cover, title, shortId, showAuthor, authorName, startQuiz } =
		useQuizBits(quiz);

	return (
		<PanCard onClick={startQuiz} className={className} style={style}>
			<div className='ph'>
				<img
					className='bg'
					src={cover}
					alt={title ?? ''}
					loading='lazy'
					data-darkzone
				/>
				<AuthorSignature showAuthor={showAuthor} authorName={authorName} />
				{fresh && (
					<span className='fresh-note'>{t('profile.quizzes.fresh')}</span>
				)}
			</div>
			<div className='tt'>
				<h3>{title}</h3>
				<span className='im'>
					{convertUnixTimestamp(created_at)} ·{' '}
					{t('profile.quizCard.questionsShort', {
						num: number_of_questions - 1,
					})}
				</span>
			</div>
			<div className='holes' aria-hidden='true' />
			<div className='stub' onClick={(e) => e.stopPropagation()}>
				<div className='bc'>
					<div className='bars' aria-hidden='true' />
					<span className='code'>
						{t('profile.quizzes.shortId', { id: shortId })}
					</span>
				</div>
				<BtnTeal type='button' $small onClick={startQuiz}>
					<IcPlayOutline size={16} />
					{t('profile.quizCard.start')}
				</BtnTeal>
			</div>
		</PanCard>
	);
};

/* list mode: proposal 11 »grande panorama« — the cover keeps its own
   untouched landscape column, title and gold-labeled fields on paper */
export const QuizTicketPanorama = ({
	quiz,
	className,
	style,
}: QuizTicketProps) => {
	const { t } = useTranslation();
	const { created_at, number_of_questions, fresh } = quiz;
	const {
		cover,
		title,
		shortId,
		showAuthor,
		authorName,
		typeLabel,
		startQuiz,
	} = useQuizBits(quiz);
	const code = t('profile.quizzes.shortId', { id: shortId });

	return (
		<Pan onClick={startQuiz} className={className} style={style}>
			<div className='ph'>
				<img
					className='bg'
					src={cover}
					alt={title ?? ''}
					loading='lazy'
					data-darkzone
				/>
				<AuthorSignature showAuthor={showAuthor} authorName={authorName} />
			</div>
			<div className='in'>
				<div className='carrier'>
					<span>{t('profile.quizzes.carrier')}</span>
					{typeLabel && <span>{typeLabel}</span>}
				</div>
				<div>
					{fresh && (
						<span className='fresh-note'>{t('profile.quizzes.fresh')}</span>
					)}
					<h3>{title}</h3>
					<div className='fields'>
						<div className='f'>
							<span className='lb'>{t('profile.quizzes.fieldCreated')}</span>
							<span className='val'>{convertUnixTimestamp(created_at)}</span>
						</div>
						<div className='f'>
							<span className='lb'>{t('profile.quizzes.fieldQuestions')}</span>
							<span className='val'>{number_of_questions - 1}</span>
						</div>
						<div className='f'>
							<span className='lb'>{t('profile.quizzes.fieldCode')}</span>
							<span className='val'>{shortId}</span>
						</div>
					</div>
				</div>
			</div>
			<div className='holes' aria-hidden='true' />
			<div className='stub' onClick={(e) => e.stopPropagation()}>
				<div className='bc'>
					<div className='bars' aria-hidden='true' />
					<span className='code'>{code}</span>
				</div>
				<BtnTeal type='button' $small onClick={startQuiz}>
					<IcPlayOutline size={16} />
					{t('profile.quizCard.start')}
				</BtnTeal>
			</div>
			<div className='rail' aria-hidden='true'>
				<span>quizzach · {new Date(created_at * 1000).getFullYear()}</span>
			</div>
		</Pan>
	);
};
